// @generated-imports
import { escapeAttribute, findGameById, focusEmulatorFrame, powerOffEmulator, showToast, toggleFullscreen, updateExitRing } from './app.js';
import { dom } from './core.js';
import { isLayerOpen, layerClosed, layerOpened } from './layers.js';
import { connectedPads, openPlayerAssignment } from './multiplayer.js';
import { endPlaySession, startPlaySession } from './platform.js';
import { SESSION_STATUS, currentSession, requestFromSession, setSessionPlayerOrder, setSessionStatus } from './session.js';
import { openStatesPanel, saveSessionState } from './states.js';
import { bumpCounter } from './stats.js';
import { rewindSession, setFastForward, timelineAvailableSeconds, timelineEnabled } from './timeline.js';
// @generated-imports
/* ===========================================================================
   MENU DE PAUSA DO PORTAL (v2.1)
   ---------------------------------------------------------------------------
   Select+Start rápido (ou o botão Home) durante o jogo abre esta camada;
   segurar Select+Start por 1,5 s desliga (o anel aparece após 0,4 s). O emulador é pausado
   (quando a build permite), o tempo de jogo para de contar e o controle
   passa a navegar o menu. Tudo operável por controle, teclado ou toque.
   ======================================================================== */

function canOpenPauseMenu() {
  const session = currentSession();
  return Boolean(session && (session.status === SESSION_STATUS.PLAYING || session.status === SESSION_STATUS.PAUSED));
}

async function setSessionPaused(session, paused) {
  if (!session || !session.capabilities.pause) return false;
  const data = await requestFromSession(session, { type: 'emutab:pause', paused: Boolean(paused) }, 'emutab:paused', 2000);
  return Boolean(data && data.ok);
}

async function openPauseMenu() {
  const session = currentSession();
  if (!canOpenPauseMenu() || !dom.pauseMenu || isLayerOpen('pause')) return false;
  session.menuOpen = true;
  setSessionStatus(session, SESSION_STATUS.PAUSED);
  endPlaySession(); // pausado não conta tempo
  updateExitRing(0);
  renderPauseMenu(session);
  dom.pauseMenu.classList.remove('is-hidden');
  layerOpened('pause', dom.pauseMenu.querySelector('[data-pause-action="resume"]'));
  const paused = await setSessionPaused(session, true);
  session.pausedByPortal = paused;
  if (dom.pauseNote) {
    dom.pauseNote.textContent = paused ? 'Jogo pausado.' : 'Esta build do emulador não aceita pausa externa — o jogo continua rodando atrás do menu.';
  }
  return true;
}

function renderPauseMenu(session) {
  const available = timelineAvailableSeconds(session);
  const canState = session.capabilities.state;
  const game = session.game || findGameById(session.gameId) || null;
  const players = game ? (game.players || 1) : 1;
  const button = (action, label, opts = {}) => `<button class="btn ${opts.primary ? 'btn-primary' : 'btn-soft'} pause-btn" type="button" data-pause-action="${action}"${opts.disabled ? ' disabled' : ''}${opts.title ? ` title="${escapeAttribute(opts.title)}"` : ''}>${label}</button>`;
  const rewindTitle = !timelineEnabled() ? 'Ligue "Voltar no tempo" em ⚙️ (desligado no modo leve)' : '';
  const items = [
    button('resume', '▶ Continuar jogando', { primary: true }),
    button('quick-save', '💾 Salvar agora (slot 1)', { disabled: !canState }),
    button('states', '⏪ Estados e slots…', { disabled: !canState }),
    button('rewind-10', '↶ Voltar 10 s', { disabled: !canState || available < 5, title: rewindTitle }),
    button('rewind-30', '↶ Voltar 30 s', { disabled: !canState || available < 20, title: rewindTitle }),
    button('rewind-60', '↶ Voltar 1 min', { disabled: !canState || available < 45, title: rewindTitle }),
  ];
  if (session.capabilities.fastForward) {
    items.push(button('fast-forward', session.fastForward ? '⏩ Avanço rápido: ligado' : '⏩ Avanço rápido: desligado'));
  }
  if (players >= 2 && session.capabilities.playerOrder && connectedPads().length >= 2) {
    items.push(button('players', '🎮 Trocar jogadores…'));
  }
  items.push(button('fullscreen', '⛶ Tela cheia'));
  items.push(button('power-off', '⏻ Desligar (salva o progresso)'));
  dom.pauseActions.innerHTML = items.join('');
  dom.pauseTitle.textContent = `☰ ${session.title}`;
  const hint = !canState ? 'Esta build do emulador não expõe estados: salvar, carregar e voltar no tempo ficam indisponíveis.'
    : !timelineEnabled() ? '"Voltar no tempo" está desligado (⚙️ ou modo leve).'
      : available ? `Dá para voltar até ${available} s.` : 'Os pontos para voltar no tempo aparecem após alguns segundos de jogo.';
  dom.pauseHint.textContent = hint;
}

function closePauseMenu(options = {}) {
  const session = currentSession();
  if (dom.pauseMenu) dom.pauseMenu.classList.add('is-hidden');
  layerClosed('pause');
  if (!session) return;
  session.menuOpen = false;
  if (session.status === SESSION_STATUS.PAUSED) {
    setSessionStatus(session, SESSION_STATUS.PLAYING);
    startPlaySession(session.gameId);
    if (session.pausedByPortal) setSessionPaused(session, false);
    session.pausedByPortal = false;
  }
  if (!options.keepFocus) focusEmulatorFrame();
}

async function handlePauseClick(event) {
  const button = event.target.closest('[data-pause-action]');
  if (!button || button.disabled) return;
  const session = currentSession();
  if (!session) { closePauseMenu(); return; }
  const action = button.dataset.pauseAction;
  if (action === 'resume') { closePauseMenu(); return; }
  if (action === 'quick-save') {
    const record = await saveSessionState(session, { kind: 'manual', slot: '1' });
    if (record) closePauseMenu();
    return;
  }
  if (action === 'states') { openStatesPanel(); return; }
  if (action.indexOf('rewind-') === 0) {
    const ok = await rewindSession(session, Number(action.split('-')[1]));
    if (ok) closePauseMenu();
    return;
  }
  if (action === 'fast-forward') {
    await setFastForward(session, !session.fastForward);
    renderPauseMenu(session);
    closePauseMenu();
    return;
  }
  if (action === 'players') {
    const order = await openPlayerAssignment(session.game || findGameById(session.gameId));
    if (order && order.length) {
      const ok = await setSessionPlayerOrder(session, order);
      showToast(ok ? `Controles atribuídos para ${order.length} jogadores.` : 'O emulador não aceitou a ordem dos controles.');
      if (ok && order.length >= 2) bumpCounter('multiSessions');
    }
    closePauseMenu();
    return;
  }
  if (action === 'fullscreen') { closePauseMenu(); toggleFullscreen(); return; }
  if (action === 'power-off') {
    closePauseMenu({ keepFocus: true });
    powerOffEmulator();
  }
}

// @generated-exports
export { canOpenPauseMenu, closePauseMenu, handlePauseClick, openPauseMenu, renderPauseMenu, setSessionPaused };
// @generated-exports
